import React, { useEffect, useMemo, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../supabase';
import { trackEvent } from '../../utils/analytics';
import { AdminLayout } from '../AdminLayout';
import { ScheduleBuilder } from '../../components/ScheduleBuilder';
import { ProgramLineItemSelector } from '../../components/ProgramLineItemSelector';
import { TagsInput } from '../../components/TagsInput';
import { AlternativeDatesManager } from '../../components/AlternativeDatesManager';
import { isValidCronExpression, ProgramType, ProgramEnrollmentStatus, getScheduleDescription, programNameToSlug, getProgramTypeDisplayName, getProgramTypeIcon, getScheduleTimeRange, defaultProgramTypeFor, isProgramTypeOfBrand, programTypeOptionsFor, getProgramTypeSectionColor, compareProgramTypes, isEnrollmentLocked } from '../../types/program';
import { AdminLineItemConfigs } from './AdminLineItemConfigs';
import { formatDateDisplay, getTodayDateString, toISODateLocal } from '../../utils/date';
import { isFlexDayCounted } from '../../utils/flexPlan';
import { Modal } from '../../components/Modal';
import { Edit, Copy, PowerOff, Power, QrCode, FileText, CalendarDays, Lock, Unlock } from 'lucide-react';
import { CampScheduleEditor } from '../components/CampScheduleEditor';
import QRCode from 'qrcode';
import { DaySelector } from '../../components/DaySelector';
import { EnrollStudent } from '../../components/EnrollStudent';
import { v4 as uuidv4 } from 'uuid';
import { SimpleMarkdownEditor } from '../../components/SimpleMarkdownEditor';
import { SimpleMarkdownPreview } from '../../components/SimpleMarkdownPreview';
import { ProgramTypeScrollNav, useProgramTypeSectionNav } from '../../components/ProgramTypeScrollNav';
import { ProgramDetailPanel } from '../components/ProgramDetailPanel';
import { sessionDayPredicate } from '../../utils/programSessionDates';
import { useQuery } from '@tanstack/react-query';
import { fetchDiscountEligibility, findEligibilityDiscount, isEligibilityDiscountSubtype, resolveDiscountVisibility } from '../../utils/eligibilityDiscounts';
import { useUrlTabState } from '../../hooks/useUrlTabState';
import { BRAND_KEYS, DEFAULT_BRAND_KEY, type BrandKey } from '../../config/brands';
import { AdminBrandTag } from '../components/AdminBrandTag';
import { BrandFilterSelect } from '../components/BrandFilterSelect';
import { matchesBrandFilter, type BrandFilter } from '../utils/brandFilter';
import { BRAND_PROFILES } from '../../config/brandProfiles';
import { LocationSelect } from '../components/LocationSelect';
import { LocationPill } from '../../components/LocationPill';
import { PROGRAM_LOCATION_EMBED, programLocationName, type ProgramLocation } from '../../types/location';
import { describeSeats, parseCapacityInput } from '../../services/programWaitlist';
import { useProgramSeats, useProgramsCapacity } from '../../hooks/useProgramSeats';
import { ProgramSeatsBadge, ProgramWaitlistPanel } from '../components/ProgramWaitlistPanel';

interface ProgramLineItem {
  id?: string;
  line_item_config_id: string;
  amount: number;
  is_optional: boolean;
  line_item_config?: {
    id: string;
    name: string;
    type: string;
    description?: string;
    subtype?: string;
  };
}

interface Program {
  id: string;
  name: string;
  description?: string;
  waiver?: string;
  type: ProgramType;
  price: number;
  line_items?: ProgramLineItem[];
  billing_type: 'recurring_monthly' | 'fixed_term' | 'per_day' | 'flex_monthly';
  /** Which brand sells this program, and therefore whose closure calendar it keeps. */
  brand_key?: BrandKey;
  active: boolean;
  /** 'closed' locks the program to new parent enrollment without stopping billing or operations. */
  enrollment_status?: ProgramEnrollmentStatus;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  schedule_cron: string;
  duration: number;
  tags: string[];
  levels?: string[] | null;
  selective_dates?: string[] | null;
  excluded_dates?: string[] | null;
  alternative_dates?: Array<{ date: string; notes: string }>;
  invoice_due_date?: string | null;
  /** fixed_term only: late joiners pick a first class and pay for the classes from it (BHCS-152). */
  prorate_late_joins?: boolean;
  /** Seats; null = unlimited (BHCS-124). */
  capacity?: number | null;
  marketing_metadata?: { website?: string; poster_image?: string } | null;
  location_id?: string | null;
  location?: ProgramLocation | ProgramLocation[] | null;
  created_at: string;
}

interface StudentSummary {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  active: boolean;
}

interface ParentSearchResult {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

type AdminProgramsTab = 'programs' | 'line-items';
const PROGRAMS_TABS: readonly AdminProgramsTab[] = ['programs', 'line-items'];

export function AdminPrograms() {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [activeSubTab, setActiveSubTab] = useUrlTabState<AdminProgramsTab>('tab', PROGRAMS_TABS, 'programs');
  const [showAddLineItemConfigForm, setShowAddLineItemConfigForm] = useState(false);
  const [programs, setPrograms] = useState<Program[]>([]);
  // Seats for the capped programs only (BHCS-124); no request while none is capped.
  const capacityById = useProgramsCapacity(programs);
  const [showInactive, setShowInactive] = useState<boolean>(false);
  const [programNameFilter, setProgramNameFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState<BrandFilter>('all');
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddingProgram, setIsAddingProgram] = useState(false);
  const [isEditingProgram, setIsEditingProgram] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [isEnrollOnBehalfOpen, setIsEnrollOnBehalfOpen] = useState<boolean>(false);
  const [enrollProgram, setEnrollProgram] = useState<Program | null>(null);
  const [isWaiverModalOpen, setIsWaiverModalOpen] = useState<boolean>(false);
  const [waiverProgram, setWaiverProgram] = useState<Program | null>(null);
  const [waiverDraft, setWaiverDraft] = useState<string>('');
  const [savingWaiver, setSavingWaiver] = useState<boolean>(false);
  const [waiverError, setWaiverError] = useState<string | null>(null);
  const [scheduleProgram, setScheduleProgram] = useState<Program | null>(null);
  const [parentSearch, setParentSearch] = useState('');
  const [parentSearchResults, setParentSearchResults] = useState<ParentSearchResult[]>([]);
  const [searchingParents, setSearchingParents] = useState(false);
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(null);
  const [selectedParentId, setSelectedParentId] = useState<string>('');
  const [selectedParentInfo, setSelectedParentInfo] = useState<ParentSearchResult | null>(null);
  const [parentStudents, setParentStudents] = useState<StudentSummary[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState<string>('');
  const [optedOutItems, setOptedOutItems] = useState<string[]>([]);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [enrollmentStartDate, setEnrollmentStartDate] = useState<string>('');
  const [sessionId, setSessionId] = useState<string>('');
  const [newProgram, setNewProgram] = useState({
    name: '',
    description: '',
    brand_key: DEFAULT_BRAND_KEY as BrandKey,
    type: defaultProgramTypeFor(DEFAULT_BRAND_KEY) as ProgramType,
    price: 0,
    line_items: [] as ProgramLineItem[],
    billing_type: 'recurring_monthly' as 'recurring_monthly' | 'fixed_term' | 'per_day' | 'flex_monthly',
    start_date: '',
    end_date: '',
    start_time: '09:00',
    end_time: '10:00',
    schedule_cron: '',
    duration: 60,
    tags: [] as string[],
    levels: [] as string[],
    selective_dates: [] as string[],
    excluded_dates: [] as string[],
    alternative_dates: [] as Array<{ date: string; notes: string }>,
    invoice_due_date: '' as string,
    prorate_late_joins: false,
    capacity: '' as string,
    marketing_website: '',
    marketing_poster_image: '',
    location_id: '',
  });

  /**
   * The programs the list is showing, after every filter.
   *
   * Extracted because this predicate was written out three times -- the type nav, the desktop
   * table and the mobile cards each had their own copy -- and the brand filter would have made
   * that four. Three copies of a filter are three chances for one to forget a clause.
   */
  const visiblePrograms = useMemo(() => {
    return (showInactive ? programs : programs.filter(p => p.active))
      .filter(p => !programNameFilter || p.name.toLowerCase().includes(programNameFilter.toLowerCase()))
      .filter(p => matchesBrandFilter(p.brand_key, brandFilter));
  }, [programs, showInactive, programNameFilter, brandFilter]);

  // Program types present in the currently-visible list, ordered like the section
  // separators below. Feeds the sticky horizontal category nav (desktop + mobile).
  const visibleProgramTypes = useMemo(() => {
    const present = Array.from(new Set(visiblePrograms.map(p => (p.type || 'other') as string)));
    return present.sort(compareProgramTypes);
  }, [visiblePrograms]);

  // Separate instances: the desktop table and mobile cards are distinct DOM trees
  // with their own scroll containers, so each needs its own registered sections.
  const desktopTypeNav = useProgramTypeSectionNav(visibleProgramTypes);
  const mobileTypeNav = useProgramTypeSectionNav(visibleProgramTypes);

  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => setCurrentUser(data.user));
  }, []);

  useEffect(() => {
    if (activeSubTab === 'programs') {
      fetchPrograms();
      fetchAvailableTags();
      setShowAddLineItemConfigForm(false);
    } else if (activeSubTab === 'line-items'){
      setIsAddingProgram(false);
      setIsEditingProgram(false);
    }
  }, [activeSubTab]);

  async function fetchPrograms() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('programs')
        .select(`
          *,
          ${PROGRAM_LOCATION_EMBED},
          pricing (
            amount,
            is_optional,
            line_item_configs:line_item_config_id (
              id,
              name,
              type,
              description,
              subtype
            )
          )
        `);
        
      if (error) throw error;

      type FetchedPricingItem = {
        amount: number;
        is_optional: boolean;
        line_item_configs: {
          id: string;
          name: string;
          type: string;
          description?: string;
          subtype?: string;
        };
      };

      const formattedPrograms = data?.map((program) => {
        const line_items: ProgramLineItem[] = program.pricing?.map((p: FetchedPricingItem) => ({
          id: p.line_item_configs.id,
          line_item_config_id: p.line_item_configs.id,
          name: p.line_item_configs.name,
          type: p.line_item_configs.type,
          description: p.line_item_configs.description,
          subtype: p.line_item_configs.subtype,
          amount: p.amount,
          is_optional: p.is_optional || false,
          line_item_config: p.line_item_configs
        })) || [];
        
        return {
          ...program,
          line_items,
          tags: program.tags || [], // Ensure tags is always an array
        };
      }) || [];

      setPrograms(formattedPrograms);
      setLoading(false);
    } catch (error: unknown) {
      console.error('Error fetching programs:', error);
      const errorObj = error as { message?: string };
      setError(errorObj.message || 'An unknown error occurred');
      setLoading(false);
    }
  }

  // Search for parents
  const searchParents = async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setParentSearchResults([]);
      return;
    }

    try {
      setSearchingParents(true);
      const { data, error } = await supabase
        .from('parents')
        .select('id, first_name, last_name, email')
        .or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`)
        .eq('active', true)
        .limit(10);

      if (error) throw error;
      setParentSearchResults(data || []);
    } catch (error) {
      console.error('Error searching parents:', error);
    } finally {
      setSearchingParents(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (parentSearch) {
        searchParents(parentSearch);
      } else {
        setParentSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [parentSearch]);

  async function openEnrollOnBehalf(program: Program) {
    setEnrollProgram(program);
    setIsEnrollOnBehalfOpen(true);
    setSelectedParentId('');
    setSelectedParentInfo(null);
    setParentStudents([]);
    setSelectedStudentId('');
    // Opt-in model: default all optional items to opted-out (excluded from billing)
    const allOptionalIds = (program.line_items || [])
      .filter(li => li.is_optional)
      .map(li => li.line_item_config_id);
    setOptedOutItems(allOptionalIds);
    setSelectedDays([]);
    setParentSearch('');
    setParentSearchResults([]);
    setSessionId(uuidv4());
    // Default start date for ongoing programs is today or program start, whichever is later
    const today = getTodayDateString();
    const start = program.start_date > today ? program.start_date : today;
    setEnrollmentStartDate(start);
  }

  async function handleParentChange(parentId: string) {
    setSelectedParentId(parentId);
    setSelectedStudentId('');
    setParentStudents([]);
    setParentSearch('');
    setParentSearchResults([]);
    if (!parentId) {
      setSelectedParentInfo(null);
      return;
    }
    
    // Find the selected parent info from search results
    const selectedParent = parentSearchResults.find(p => p.id === parentId);
    if (selectedParent) {
      setSelectedParentInfo(selectedParent);
    }
    
    const { data: studentParents } = await supabase
      .from('student_parents')
      .select('students (id, first_name, last_name, date_of_birth, active)')
      .eq('parent_id', parentId);
    const students: StudentSummary[] = (studentParents || []).flatMap((sp: { students: StudentSummary[] }) => sp.students);
    // Only active students
    setParentStudents(students.filter(s => s.active));
  }

  function closeEnrollOnBehalf() {
    setIsEnrollOnBehalfOpen(false);
    setEnrollProgram(null);
    setSelectedParentId('');
    setSelectedParentInfo(null);
    setParentStudents([]);
    setSelectedStudentId('');
    setOptedOutItems([]);
    setSelectedDays([]);
  }

  const enrollmentCondition = useMemo<'upcoming' | 'ongoing' | 'ended' | 'loading'>(() => {
    if (!enrollProgram) return 'loading';
    const today = getTodayDateString();
    if (enrollProgram.end_date < today) return 'ended';
    if (enrollProgram.start_date > today) return 'upcoming';
    return 'ongoing';
  }, [enrollProgram]);

  async function fetchAvailableTags() {
    try {
      const { data, error } = await supabase
        .from('programs')
        .select('tags');
        
      if (error) throw error;

      // Flatten all tags and remove duplicates
      const allTags = data?.reduce((acc: string[], program) => {
        if (program.tags && Array.isArray(program.tags)) {
          return [...acc, ...program.tags];
        }
        return acc;
      }, []) || [];

      // Remove duplicates and sort
      const uniqueTags = [...new Set(allTags)].sort((a, b) => 
        a.toLowerCase().localeCompare(b.toLowerCase())
      );

      setAvailableTags(uniqueTags);
    } catch (error: unknown) {
      console.error('Error fetching available tags:', error);
    }
  }

  function calculateDuration(startTime: string, endTime: string): number {
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    
    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;
    
    return endTotalMinutes - startTotalMinutes;
  }

  function handleTimeChange(field: 'start_time' | 'end_time', value: string) {
    const updatedProgram = { ...newProgram, [field]: value };
    const duration = calculateDuration(updatedProgram.start_time, updatedProgram.end_time);
    setNewProgram({ ...updatedProgram, duration });
  }

  function handleProgramEditClick(program: Program) {
    setSelectedProgram(program);
    const formatDate = (dateString: string) => {
      if (!dateString) return '';
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return toISODateLocal(date);
    };

    // Extract hour and minute from the cron string
    let actualStartTime = '09:00'; // Default if cron is weird
    if (program.schedule_cron) {
      const cronParts = program.schedule_cron.split(' ');
      if (cronParts.length === 5) {
        const minute = cronParts[0];
        const hour = cronParts[1];
        if (minute !== '*' && hour !== '*') {
          actualStartTime = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
        }
      }
    }

    // Calculate end time based on the actual start time and duration
    const endTime = new Date(new Date(`2000-01-01T${actualStartTime}`).getTime() + program.duration * 60000)
      .toTimeString().slice(0, 5);

    setNewProgram({
      name: program.name,
      description: program.description || '',
      brand_key: (program.brand_key ?? DEFAULT_BRAND_KEY) as BrandKey,
      type: program.type as ProgramType,
      price: program.line_items?.filter(item => !isEligibilityDiscountSubtype(item.line_item_config?.subtype)).reduce((sum, item) => sum + item.amount, 0) || 0,
      line_items: program.line_items?.map(item => ({
        line_item_config_id: item.id || item.line_item_config_id,
        amount: item.amount,
        is_optional: item.is_optional || false,
        line_item_config: item.line_item_config,
      })) || [],
      billing_type: program.billing_type as 'recurring_monthly' | 'fixed_term' | 'per_day' | 'flex_monthly',
      start_date: formatDate(program.start_date),
      end_date: formatDate(program.end_date),
      start_time: actualStartTime, // Use parsed start time
      end_time: endTime,           // Use end time calculated from actualStartTime
      schedule_cron: program.schedule_cron,
      duration: program.duration,
      tags: program.tags || [],
      levels: program.levels || [],
      selective_dates: program.selective_dates || [],
      excluded_dates: program.excluded_dates || [],
      alternative_dates: program.alternative_dates || [],
      invoice_due_date: program.invoice_due_date ?? '',
      prorate_late_joins: program.prorate_late_joins ?? false,
      capacity: program.capacity != null ? String(program.capacity) : '',
      marketing_website: program.marketing_metadata?.website ?? '',
      marketing_poster_image: program.marketing_metadata?.poster_image ?? '',
      location_id: program.location_id ?? '',
    });
    setIsEditingProgram(true);
    setActiveSubTab('programs'); 
    setShowAddLineItemConfigForm(false);
  }

  function handleCloneProgram(program: Program) {
    const formatDate = (dateString: string) => {
      if (!dateString) return '';
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return toISODateLocal(date);
    };

    // Extract hour and minute from the cron string
    let actualStartTime = '09:00'; // Default if cron is weird
    if (program.schedule_cron) {
      const cronParts = program.schedule_cron.split(' ');
      if (cronParts.length === 5) {
        const minute = cronParts[0];
        const hour = cronParts[1];
        if (minute !== '*' && hour !== '*') {
          actualStartTime = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
        }
      }
    }

    // Calculate end time based on the actual start time and duration
    const endTime = new Date(new Date(`2000-01-01T${actualStartTime}`).getTime() + program.duration * 60000)
      .toTimeString().slice(0, 5);

    setNewProgram({
      name: `${program.name} (Copy)`, // Add "(Copy)" to indicate it's a clone
      description: program.description || '', // Include description field
      // Carried explicitly: a cloned Kaleido program used to come back as BHCS, because the
      // insert never wrote a brand and the column defaulted.
      brand_key: (program.brand_key ?? DEFAULT_BRAND_KEY) as BrandKey,
      type: program.type as ProgramType,
      price: program.line_items?.filter(item => !isEligibilityDiscountSubtype(item.line_item_config?.subtype)).reduce((sum, item) => sum + item.amount, 0) || 0,
      line_items: program.line_items?.map(item => ({
        line_item_config_id: item.id || item.line_item_config_id,
        amount: item.amount,
        is_optional: item.is_optional || false,
        line_item_config: item.line_item_config,
      })) || [],
      billing_type: program.billing_type as 'recurring_monthly' | 'fixed_term' | 'per_day' | 'flex_monthly',
      start_date: formatDate(program.start_date),
      end_date: formatDate(program.end_date),
      start_time: actualStartTime, // Use parsed start time
      end_time: endTime,           // Use end time calculated from actualStartTime
      schedule_cron: program.schedule_cron,
      duration: program.duration,
      tags: program.tags || [],
      levels: program.levels || [],
      selective_dates: program.selective_dates || [],
      excluded_dates: program.excluded_dates || [],
      alternative_dates: program.alternative_dates || [],
      invoice_due_date: program.invoice_due_date ?? '',
      prorate_late_joins: program.prorate_late_joins ?? false,
      capacity: program.capacity != null ? String(program.capacity) : '',
      marketing_website: program.marketing_metadata?.website ?? '',
      marketing_poster_image: program.marketing_metadata?.poster_image ?? '',
      location_id: program.location_id ?? '',
    });
    setIsAddingProgram(true); // Set to adding mode, not editing
    setSelectedProgram(null); // Clear selected program since we're creating new
    setActiveSubTab('programs'); 
    setShowAddLineItemConfigForm(false);
  }
  
  function handleAddProgramClick() {
    setIsAddingProgram(true);
    setActiveSubTab('programs'); 
    setShowAddLineItemConfigForm(false);
  }

  function handleCancelEdit() {
    setIsEditingProgram(false);
    setIsAddingProgram(false);
    setSelectedProgram(null);
    setNewProgram({
      name: '',
      description: '',
      brand_key: DEFAULT_BRAND_KEY as BrandKey,
      type: defaultProgramTypeFor(DEFAULT_BRAND_KEY) as ProgramType,
      price: 0,
      line_items: [],
      billing_type: 'recurring_monthly',
      start_date: '',
      end_date: '',
      start_time: '09:00',
      end_time: '10:00',
      schedule_cron: '',
      duration: 60,
      tags: [],
      levels: [],
      selective_dates: [],
    excluded_dates: [],
      alternative_dates: [],
      invoice_due_date: '',
      prorate_late_joins: false,
      capacity: '',
      marketing_website: '',
      marketing_poster_image: '',
      location_id: '',
    });
    setFormError(null);
  }

  async function handleToggleProgramStatus(programId: string, currentStatus: boolean) {
    try {
      const { error } = await supabase
        .from('programs')
        .update({ active: !currentStatus })
        .eq('id', programId);

      if (error) throw error;
      fetchPrograms();
      trackEvent({ eventType: 'admin_program_update', user: currentUser, userType: 'admin', entityType: 'program', entityId: programId, metadata: { action: currentStatus ? 'deactivate' : 'activate' } });
    } catch (error: unknown) {
      console.error('Error toggling program status:', error);
      const errorObj = error as { message?: string };
      setError(errorObj.message || 'An unknown error occurred');
    }
  }

  /**
   * Lock/unlock a program to new parent enrollment. Deliberately separate from
   * handleToggleProgramStatus: `active` controls whether the program runs at all (billing, staff
   * rosters, attendance), so closing signups must not touch it. A locked program keeps invoicing
   * its existing enrollments, and admins can still enroll on behalf.
   */
  async function handleToggleEnrollmentStatus(programId: string, currentlyLocked: boolean) {
    const next: ProgramEnrollmentStatus = currentlyLocked ? 'open' : 'closed';
    try {
      const { error } = await supabase
        .from('programs')
        .update({ enrollment_status: next })
        .eq('id', programId);

      if (error) throw error;
      fetchPrograms();
      trackEvent({ eventType: 'admin_program_update', user: currentUser, userType: 'admin', entityType: 'program', entityId: programId, metadata: { action: currentlyLocked ? 'unlock_enrollment' : 'lock_enrollment' } });
    } catch (error: unknown) {
      console.error('Error toggling program enrollment status:', error);
      const errorObj = error as { message?: string };
      setError(errorObj.message || 'An unknown error occurred');
    }
  }

  async function copySlugUrl(programName: string) {
    try {
      const slug = programNameToSlug(programName);
      const fullUrl = `${window.location.origin}/programs/${slug}`;
      await navigator.clipboard.writeText(fullUrl);
      setCopySuccess(`Copied: ${fullUrl}`);
      setTimeout(() => setCopySuccess(null), 3000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      setCopySuccess('Failed to copy to clipboard');
      setTimeout(() => setCopySuccess(null), 3000);
    }
  }

  async function generateQRCode(programName: string) {
    try {
      const slug = programNameToSlug(programName);
      const fullUrl = `${window.location.origin}/programs/${slug}`;
      const qrDataUrl = await QRCode.toDataURL(fullUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeDataUrl(qrDataUrl);
      setQrCodeUrl(fullUrl);
      setShowQRModal(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
      setFormError('Failed to generate QR code');
      setTimeout(() => setFormError(null), 3000);
    }
  }

  function openWaiverModal(program: Program) {
    setWaiverProgram(program);
    setWaiverDraft(program.waiver || '');
    setWaiverError(null);
    setIsWaiverModalOpen(true);
  }

  function closeWaiverModal() {
    setIsWaiverModalOpen(false);
    setWaiverProgram(null);
    setWaiverDraft('');
    setWaiverError(null);
  }

  async function saveWaiver() {
    if (!waiverProgram) return;
    try {
      setSavingWaiver(true);
      setWaiverError(null);
      const { error: wErr } = await supabase
        .from('programs')
        .update({ waiver: waiverDraft })
        .eq('id', waiverProgram.id);
      if (wErr) throw wErr;
      setIsWaiverModalOpen(false);
      setWaiverProgram(null);
      setWaiverDraft('');
      await fetchPrograms();
    } catch (e) {
      const err = e as { message?: string };
      setWaiverError(err.message || 'Failed to save waiver');
    } finally {
      setSavingWaiver(false);
    }
  }

  async function handleAddProgram(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    try {
      if (!newProgram.name) { setFormError('Program name is required'); return; }
      if (!newProgram.start_date || !newProgram.end_date) { setFormError('Start date and end date are required'); return; }
      if (!isValidCronExpression(newProgram.schedule_cron)) { setFormError('Invalid schedule expression'); return; }
      const capacity = parseCapacityInput(newProgram.capacity);
      if (!capacity.ok) { setFormError(capacity.error); return; }

      const { data, error } = await supabase
        .from('programs')
        .insert([{
          name: newProgram.name,
          description: newProgram.description || null, // Include description field
          brand_key: newProgram.brand_key,
          type: newProgram.type,
          price: newProgram.line_items.filter(item => !isEligibilityDiscountSubtype(item.line_item_config?.subtype)).reduce((sum, item) => sum + item.amount, 0),
          billing_type: newProgram.billing_type,
          active: true,
          start_date: newProgram.start_date.split('T')[0],
          end_date: newProgram.end_date.split('T')[0],
          schedule_cron: newProgram.schedule_cron,
          duration: newProgram.duration,
          tags: newProgram.tags,
          levels: newProgram.levels && newProgram.levels.length > 0 ? newProgram.levels : null,
          selective_dates: newProgram.billing_type === 'per_day' && newProgram.selective_dates.length > 0 ? newProgram.selective_dates : null,
          excluded_dates: newProgram.excluded_dates.length > 0 ? [...new Set(newProgram.excluded_dates)].sort() : null,
          alternative_dates: newProgram.alternative_dates,
          invoice_due_date: newProgram.type === 'camp' && newProgram.invoice_due_date ? newProgram.invoice_due_date : null,
          prorate_late_joins: newProgram.billing_type === 'fixed_term' && newProgram.prorate_late_joins,
          capacity: capacity.value,
          marketing_metadata: (newProgram.marketing_website || newProgram.marketing_poster_image)
            ? { website: newProgram.marketing_website || undefined, poster_image: newProgram.marketing_poster_image || undefined }
            : null,
          location_id: newProgram.location_id || null,
        }])
        .select();
      if (error) throw error;
      if (newProgram.line_items && newProgram.line_items.length > 0 && data?.[0]?.id) {
        const programId = data[0].id;
        for (const item of newProgram.line_items) {
          const { error: pricingError } = await supabase
            .from('pricing')
            .insert([{ program_id: programId, line_item_config_id: item.line_item_config_id, amount: item.amount }]);
          if (pricingError) throw pricingError;
        }
      }
      setSuccessMessage('Program added successfully!');
      trackEvent({ eventType: 'admin_program_create', user: currentUser, userType: 'admin', entityType: 'program', entityId: data?.[0]?.id, metadata: { program_name: newProgram.name, billing_type: newProgram.billing_type, type: newProgram.type } });
      handleCancelEdit();
      fetchPrograms();
      fetchAvailableTags(); // Refresh available tags
    } catch (error: unknown) {
      console.error('Error adding program:', error);
      const errorObj = error as { message?: string };
      setFormError(errorObj.message || 'An unknown error occurred');
    }
  }

  async function handleEditProgram(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedProgram) return;
    setFormError(null);
    try {
      if (!newProgram.name) { setFormError('Program name is required'); return; }
      if (!newProgram.start_date || !newProgram.end_date) { setFormError('Start date and end date are required'); return; }
      if (!isValidCronExpression(newProgram.schedule_cron)) { setFormError('Invalid schedule expression'); return; }
      const capacity = parseCapacityInput(newProgram.capacity);
      if (!capacity.ok) { setFormError(capacity.error); return; }

      const { error: programError } = await supabase
        .from('programs')
        .update({
          name: newProgram.name, 
          description: newProgram.description || null, // Include description field
          brand_key: newProgram.brand_key,
          type: newProgram.type,
          price: newProgram.line_items.filter(item => !isEligibilityDiscountSubtype(item.line_item_config?.subtype)).reduce((sum, item) => sum + item.amount, 0),
          billing_type: newProgram.billing_type, start_date: newProgram.start_date.split('T')[0],
          end_date: newProgram.end_date.split('T')[0], schedule_cron: newProgram.schedule_cron, duration: newProgram.duration,
          tags: newProgram.tags,
          levels: newProgram.levels && newProgram.levels.length > 0 ? newProgram.levels : null,
          selective_dates: newProgram.billing_type === 'per_day' && newProgram.selective_dates.length > 0 ? newProgram.selective_dates : null,
          excluded_dates: newProgram.excluded_dates.length > 0 ? [...new Set(newProgram.excluded_dates)].sort() : null,
          alternative_dates: newProgram.alternative_dates,
          invoice_due_date: newProgram.type === 'camp' && newProgram.invoice_due_date ? newProgram.invoice_due_date : null,
          prorate_late_joins: newProgram.billing_type === 'fixed_term' && newProgram.prorate_late_joins,
          capacity: capacity.value,
          marketing_metadata: (newProgram.marketing_website || newProgram.marketing_poster_image)
            ? { website: newProgram.marketing_website || undefined, poster_image: newProgram.marketing_poster_image || undefined }
            : null,
          location_id: newProgram.location_id || null,
        })
        .eq('id', selectedProgram.id);
      if (programError) throw programError;

      const { data: existingPricing, error: pricingError } = await supabase.from('pricing').select('line_item_config_id').eq('program_id', selectedProgram.id);
      if (pricingError) throw pricingError;
      const existingLineItemIds = new Set(existingPricing?.map(p => p.line_item_config_id) || []);
      const newLineItemIds = new Set(newProgram.line_items.map(item => item.line_item_config_id));
      const idsToRemove = [...existingLineItemIds].filter(id => !newLineItemIds.has(id));
      if (idsToRemove.length > 0) {
        const { error: deleteError } = await supabase.from('pricing').delete().eq('program_id', selectedProgram.id).in('line_item_config_id', idsToRemove);
        if (deleteError) throw deleteError;
      }
      for (const item of newProgram.line_items) {
        const { error: upsertError } = await supabase.from('pricing').upsert([
          { 
            program_id: selectedProgram.id, 
            line_item_config_id: item.line_item_config_id, 
            amount: item.amount,
            is_optional: item.is_optional 
          }
        ], { onConflict: 'program_id,line_item_config_id' });
        if (upsertError) throw upsertError;
      }
      setSuccessMessage('Program updated successfully!');
      trackEvent({ eventType: 'admin_program_update', user: currentUser, userType: 'admin', entityType: 'program', entityId: selectedProgram.id, metadata: { program_name: newProgram.name } });
      handleCancelEdit();
      fetchPrograms();
      fetchAvailableTags(); // Refresh available tags
    } catch (error: unknown) {
      console.error('Error updating program:', error);
      const errorObj = error as { message?: string };
      setFormError(errorObj.message || 'An unknown error occurred');
    }
  }

  if (error && activeSubTab === 'programs') {
    return (
      <AdminLayout>
        <div className="mb-4 p-4 bg-red-50 text-red-600 rounded">
          {error}
        </div>
      </AdminLayout>
    );
  }

  // The discounted price beside the regular one, e.g. "BHCS: $675.00" or "Foster City: $180.00"
  // (price with the program's eligibility discount applied). Null without such a discount.
  function getDiscountedPriceLabel(program: Program): string | null {
    const discount = findEligibilityDiscount((program.line_items || []).map(li => li.line_item_config?.subtype));
    if (!discount) return null;
    const discountTotal = (program.line_items || [])
      .filter(li => isEligibilityDiscountSubtype(li.line_item_config?.subtype))
      .reduce((sum, li) => sum + li.amount, 0);
    return `${discount.shortLabel}: $${(program.price + discountTotal).toFixed(2)}`;
  }

  return (
    <AdminLayout>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
        <div className="border-b border-gray-200 flex-shrink-0">
          <nav className="-mb-px flex items-center justify-between">
            <div className="flex">
              <button
                className={`whitespace-nowrap py-3 px-4 text-sm font-medium ${
                  activeSubTab === 'programs'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveSubTab('programs')}
              >
                Programs
              </button>
              <button
                className={`whitespace-nowrap py-3 px-4 text-sm font-medium ${
                  activeSubTab === 'line-items'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveSubTab('line-items')}
              >
                Line Item Configs
              </button>
            </div>
            {activeSubTab === 'programs' && (
              <>
                <button
                  onClick={handleAddProgramClick}
                  className="sm:hidden mr-3 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl leading-none hover:bg-blue-700 transition-colors"
                  aria-label="New program"
                >
                  +
                </button>
                <button
                  onClick={handleAddProgramClick}
                  className="hidden sm:block mr-4 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Add Program
                </button>
              </>
            )}
            {activeSubTab === 'line-items' && (
              <>
                <button
                  onClick={() => setShowAddLineItemConfigForm(true)}
                  className="sm:hidden mr-3 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl leading-none hover:bg-blue-700 transition-colors"
                  aria-label="New line item config"
                >
                  +
                </button>
                <button
                  onClick={() => setShowAddLineItemConfigForm(true)}
                  className="hidden sm:block mr-4 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Add Configuration
                </button>
              </>
            )}
          </nav>
        </div>

        {activeSubTab === 'programs' && (
          <>
            {successMessage && (
              <div className="m-4 p-4 bg-green-50 text-green-700 rounded-md">
                {successMessage}
              </div>
            )}
            {copySuccess && (
              <div className="m-4 p-4 bg-blue-50 text-blue-700 rounded-md">
                {copySuccess}
              </div>
            )}

            <Modal
              isOpen={isAddingProgram || isEditingProgram}
              onClose={handleCancelEdit}
              title={isAddingProgram ? 'Add New Program' : 'Edit Program'}
            >
              <form onSubmit={isAddingProgram ? handleAddProgram : handleEditProgram} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Program Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      value={newProgram.name}
                      onChange={(e) => setNewProgram({ ...newProgram, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label htmlFor="brand" className="block text-sm font-medium text-gray-700">
                      Brand
                    </label>
                    <select
                      id="brand"
                      className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      value={newProgram.brand_key}
                      onChange={(e) => {
                        const brand = e.target.value as BrandKey;
                        // The type vocabularies do not overlap, so a type from the old brand
                        // cannot survive the switch -- it would leave the picker below showing
                        // a value it does not offer, which is the blank-dropdown bug this
                        // screen already had. Keep the type only if the new brand sells it.
                        setNewProgram(prev => ({
                          ...prev,
                          brand_key: brand,
                          type: (isProgramTypeOfBrand(prev.type, brand)
                            ? prev.type
                            : defaultProgramTypeFor(brand)) as ProgramType,
                        }));
                      }}
                    >
                      {BRAND_KEYS.map((key) => (
                        <option key={key} value={key}>{BRAND_PROFILES[key].shortName}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Which catalog parents find this in. Changing it moves the program between
                      them.
                    </p>
                  </div>
                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                      Program Type
                    </label>
                    <select
                      id="type"
                      className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      value={newProgram.type}
                      onChange={(e) => setNewProgram({ ...newProgram, type: e.target.value as ProgramType })}
                    >
                      {/* This brand's vocabulary only. Offering the full set is what let a
                          Kaleido program's type render blank and be silently rewritten. */}
                      {programTypeOptionsFor(newProgram.brand_key).map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <LocationSelect
                    value={newProgram.location_id}
                    onChange={(locationId) => setNewProgram(prev => ({ ...prev, location_id: locationId }))}
                    onCatalogChanged={() => { void fetchPrograms(); }}
                  />
                </div>
                
                {/* Program Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Program Description
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Optional detailed description of the program content, objectives, and what students will learn.
                  </p>
                  {/* Schedule prose in here used to be the only place a parent could read the
                      term, and it went stale the moment anyone changed a date. The program's own
                      fields now render that block on both the program and enrollment pages, so a
                      hand-typed copy can only ever contradict it. */}
                  <p className="text-xs text-amber-700 mb-2">
                    Do not repeat the meeting day, class time, start/end dates, session count, or
                    no-class dates here — those are read from the fields below (and the closure
                    calendar) and shown to parents automatically.
                  </p>
                  <textarea
                    id="description"
                    rows={4}
                    className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    placeholder="Enter a detailed description of the program..."
                    value={newProgram.description}
                    onChange={(e) => setNewProgram({ ...newProgram, description: e.target.value })}
                  />
                </div>
                
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label htmlFor="billing_type" className="block text-sm font-medium text-gray-700">
                      Billing Type
                    </label>
                    <select
                      id="billing_type"
                      className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      value={newProgram.billing_type}
                      onChange={(e) => {
                        const billingType = e.target.value as 'recurring_monthly' | 'fixed_term' | 'per_day' | 'flex_monthly';
                        setNewProgram({ 
                          ...newProgram, 
                          billing_type: billingType,
                          // Clear alternative dates if not recurring_monthly
                          alternative_dates: billingType === 'recurring_monthly' ? newProgram.alternative_dates : []
                        });
                      }}
                    >
                      <option value="recurring_monthly">Monthly Recurring</option>
                      <option value="fixed_term">Fixed Term</option>
                      <option value="per_day">Per Day</option>
                      <option value="flex_monthly">Flex Monthly</option>
                    </select>
                    {newProgram.billing_type === 'flex_monthly' && (
                      <p className="mt-1 text-xs text-gray-500">
                        Flex bills monthly for the days confirmed in each month&apos;s plan. The
                        <strong> Tuition</strong> line item below is the <strong>per-day</strong> rate.
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
                      Start Date
                    </label>
                    <input
                      type="date"
                      id="start_date"
                      className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      value={newProgram.start_date}
                      onChange={(e) => setNewProgram({ ...newProgram, start_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <label htmlFor="end_date" className="block text-sm font-medium text-gray-700">
                      End Date
                    </label>
                    <input
                      type="date"
                      id="end_date"
                      className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      value={newProgram.end_date}
                      onChange={(e) => setNewProgram({ ...newProgram, end_date: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="start_time" className="block text-sm font-medium text-gray-700">
                      Start Time
                    </label>
                    <input
                      type="time"
                      id="start_time"
                      className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-40 shadow-sm sm:text-sm border-gray-300 rounded-md"
                      value={newProgram.start_time}
                      onChange={(e) => handleTimeChange('start_time', e.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="end_time" className="block text-sm font-medium text-gray-700">
                      End Time
                    </label>
                    <input
                      type="time"
                      id="end_time"
                      className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-40 shadow-sm sm:text-sm border-gray-300 rounded-md"
                      value={newProgram.end_time}
                      onChange={(e) => handleTimeChange('end_time', e.target.value)}
                    />
                  </div>
                </div>
                
                {/* Alternative Dates - Only show for recurring monthly programs */}
                {newProgram.billing_type === 'recurring_monthly' && (
                  <div>
                    <AlternativeDatesManager
                      alternativeDates={newProgram.alternative_dates}
                      onChange={(dates) => setNewProgram({ ...newProgram, alternative_dates: dates })}
                      programStartDate={newProgram.start_date}
                      programEndDate={newProgram.end_date}
                    />
                  </div>
                )}

                {/* Invoice Due Date - Camp programs only */}
                {newProgram.type === 'camp' && (
                  <div className="max-w-xs">
                    <label htmlFor="invoice_due_date" className="block text-sm font-medium text-gray-700">
                      Invoice Due Date
                    </label>
                    <p className="text-xs text-gray-500 mt-0.5 mb-1">
                      Date invoices are due for this camp. Leave blank to use the program start date.
                    </p>
                    <input
                      type="date"
                      id="invoice_due_date"
                      className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      value={newProgram.invoice_due_date}
                      onChange={(e) => setNewProgram({ ...newProgram, invoice_due_date: e.target.value })}
                    />
                  </div>
                )}
                
                {/* Late joiners pay by the class (BHCS-152) - fixed-term only */}
                {newProgram.billing_type === 'fixed_term' && (
                  <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      id="prorate_late_joins"
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={newProgram.prorate_late_joins}
                      onChange={(e) => setNewProgram({ ...newProgram, prorate_late_joins: e.target.checked })}
                    />
                    <span>
                      Late joiners pay by the class
                      <span className="block text-xs text-gray-500">
                        Parents pick their child&apos;s first class, and the invoice bills only the classes from it to
                        the end of the term. Every line scales except materials, discounts included. Leave this off for
                        camps and one-day workshops.
                      </span>
                    </span>
                  </label>
                )}

                {/* Seats (BHCS-124). Blank = unlimited, which is every program until an admin sets one. */}
                <div>
                  <label htmlFor="capacity" className="block text-sm font-medium text-gray-700">
                    Capacity
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5 mb-1">
                    Seats for children. When it is full, parents can join a waitlist instead of enrolling, and you
                    offer seats from the program&apos;s Waitlist panel. Admins can still enroll over it. Leave blank for
                    unlimited.
                  </p>
                  <input
                    type="number"
                    id="capacity"
                    min={1}
                    step={1}
                    inputMode="numeric"
                    placeholder="Unlimited"
                    className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-40 shadow-sm sm:text-sm border-gray-300 rounded-md"
                    value={newProgram.capacity}
                    onChange={(e) => setNewProgram({ ...newProgram, capacity: e.target.value })}
                  />
                </div>

                <div>
                  <label htmlFor="schedule" className="block text-sm font-medium text-gray-700">
                    Schedule
                  </label>
                  <ScheduleBuilder
                    value={newProgram.schedule_cron}
                    onChange={(cron) => setNewProgram({ ...newProgram, schedule_cron: cron })}
                    startTime={newProgram.start_time}
                  />
                </div>
                
                {newProgram.duration < 15 && (
                  <div className="text-red-600 text-sm">
                    Duration must be at least 15 minutes
                  </div>
                )}
                
                {/* Program-specific no-class days. Any program type: a vendor can diverge
                    from the school calendar whatever its billing is. */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Program no-class dates
                  </label>
                  <p className="text-xs text-gray-600 mb-2">
                    Days <strong>this program alone</strong> does not meet &mdash; a vendor taking its
                    own day off. Days the whole school is closed belong on the{' '}
                    <a href="/admin-app/school-calendar" className="text-blue-600 hover:underline">School Calendar</a>{' '}
                    instead: putting one here works, but goes stale if the school later moves that date.
                  </p>
                  <DaySelector
                    startDate={newProgram.start_date || ''}
                    endDate={newProgram.end_date || ''}
                    selectedDays={newProgram.excluded_dates}
                    onChange={(ds) => setNewProgram({ ...newProgram, excluded_dates: ds })}
                    minSelections={0}
                    scheduleCron={newProgram.schedule_cron}
                  />
                </div>

                {/* Selective Dates - Only for per_day programs */}
                {newProgram.billing_type === 'per_day' && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Selective Dates (parents can only choose these)
                    </label>
                    <p className="text-xs text-gray-600 mb-2">
                      Pick allowed dates between start and end that match the schedule. Leave empty to allow all scheduled days.
                    </p>
                    <DaySelector
                      startDate={newProgram.start_date || ''}
                      endDate={newProgram.end_date || ''}
                      selectedDays={newProgram.selective_dates}
                      onChange={(ds) => setNewProgram({ ...newProgram, selective_dates: ds })}
                      minSelections={0}
                      scheduleCron={newProgram.schedule_cron}
                    />
                  </div>
                )}
                
                <div>
                  <label htmlFor="line_items" className="block text-sm font-medium text-gray-700">
                    Pricing & Line Items
                  </label>
                  <ProgramLineItemSelector
                    lineItems={newProgram.line_items}
                    onChange={(items) => setNewProgram({ ...newProgram, line_items: items })}
                    billingType={newProgram.billing_type}
                    brandKey={newProgram.brand_key}
                  />
                </div>

                <div>
                  <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
                    Program Tags
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Add tags to categorize and organize programs. Tags help with filtering and grouping similar programs.
                  </p>
                  <TagsInput
                    value={newProgram.tags}
                    onChange={(tags) => setNewProgram({ ...newProgram, tags })}
                    availableTags={availableTags}
                    placeholder="Add tags to categorize this program..."
                    maxTags={10}
                  />
                </div>

                <div>
                  <label htmlFor="levels" className="block text-sm font-medium text-gray-700">
                    Program Levels (optional)
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Define selectable levels (e.g., Beginner, Intermediate, Advanced). If set, parents must choose one during enrollment.
                  </p>
                  <TagsInput
                    value={newProgram.levels}
                    onChange={(levels) => setNewProgram({ ...newProgram, levels })}
                    placeholder="Add levels..."
                    maxTags={10}
                  />
                </div>

                {/* Marketing Metadata */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="marketing_website" className="block text-sm font-medium text-gray-700">
                      Website URL (optional)
                    </label>
                    <p className="text-xs text-gray-500 mb-1">Official program or registration page URL.</p>
                    <input
                      type="url"
                      id="marketing_website"
                      className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      placeholder="https://example.com/program"
                      value={newProgram.marketing_website}
                      onChange={(e) => setNewProgram({ ...newProgram, marketing_website: e.target.value })}
                    />
                  </div>
                  <div>
                    <label htmlFor="marketing_poster_image" className="block text-sm font-medium text-gray-700">
                      Poster Image Path (optional)
                    </label>
                    <p className="text-xs text-gray-500 mb-1">Static asset path or URL for the program poster.</p>
                    <input
                      type="text"
                      id="marketing_poster_image"
                      className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      placeholder="/images/programs/poster.jpg"
                      value={newProgram.marketing_poster_image}
                      onChange={(e) => setNewProgram({ ...newProgram, marketing_poster_image: e.target.value })}
                    />
                  </div>
                </div>

                {formError && (
                  <div className="text-red-600 text-sm">{formError}</div>
                )}
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Save
                  </button>
                </div>
              </form>
            </Modal>

            {/* QR Code Modal */}
            <Modal
              isOpen={showQRModal}
              onClose={() => setShowQRModal(false)}
              title="Program QR Code"
            >
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-4">
                    Scan this QR code to access the program registration page
                  </p>
                  {qrCodeDataUrl && (
                    <div className="flex justify-center">
                      <img 
                        src={qrCodeDataUrl} 
                        alt="Program QR Code" 
                        className="border border-gray-200 rounded-lg"
                        width="300"
                        height="300"
                        decoding="async"
                      />
                    </div>
                  )}
                  <div className="mt-4">
                    <p className="text-xs text-gray-500 mb-2">Program URL:</p>
                    <p className="text-sm font-mono text-gray-700 break-all bg-gray-50 p-2 rounded">
                      {qrCodeUrl}
                    </p>
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowQRModal(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(qrCodeUrl);
                        setCopySuccess('URL copied to clipboard!');
                        setTimeout(() => setCopySuccess(null), 3000);
                      } catch (error) {
                        console.error('Error copying URL:', error);
                        setFormError('Failed to copy URL');
                        setTimeout(() => setFormError(null), 3000);
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Copy URL
                  </button>
                </div>
              </div>
            </Modal>

            {/* Attach Waiver Modal */}
            <Modal
              isOpen={isWaiverModalOpen}
              onClose={closeWaiverModal}
              title={waiverProgram ? `Attach Waiver — ${waiverProgram.name}` : 'Attach Waiver'}
            >
              <div className="space-y-4">
                <p className="text-xs text-gray-600">
                  Enter waiver text using basic markdown. Parents will be asked to acknowledge and sign during enrollment.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-medium text-gray-700 mb-1">Editor</div>
                    <SimpleMarkdownEditor
                      value={waiverDraft}
                      onChange={setWaiverDraft}
                      placeholder="Enter waiver in markdown..."
                      minRows={14}
                    />
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-700 mb-1">Preview</div>
                    <div className="p-3 border rounded-md bg-white min-h-[220px]">
                      <SimpleMarkdownPreview value={waiverDraft} />
                    </div>
                  </div>
                </div>
                {waiverError && (
                  <div className="text-red-600 text-sm">{waiverError}</div>
                )}
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeWaiverModal}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                    disabled={savingWaiver}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveWaiver}
                    className={`px-4 py-2 rounded-md text-white ${savingWaiver ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'} transition-colors`}
                    disabled={savingWaiver}
                  >
                    {savingWaiver ? 'Saving...' : 'Save Waiver'}
                  </button>
                </div>
              </div>
            </Modal>

            {/* Camp Schedule Editor */}
            <CampScheduleEditor
              isOpen={scheduleProgram !== null}
              onClose={() => setScheduleProgram(null)}
              program={scheduleProgram ? { id: scheduleProgram.id, name: scheduleProgram.name } : null}
            />

            <div className="flex flex-col lg:flex-1 lg:min-h-0">
              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-2 px-4 sm:px-6 py-3 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                <input
                  type="text"
                  placeholder="Filter by program name..."
                  className="flex-1 min-w-0 px-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={programNameFilter}
                  onChange={(e) => setProgramNameFilter(e.target.value)}
                />
                {/* Defaults to every brand: an admin list should not hide rows by default, and
                    "All brands" keeps this screen exactly as it was for a single-brand org. */}
                <BrandFilterSelect value={brandFilter} onChange={setBrandFilter} />
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-sm text-gray-600">Include inactive</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={showInactive}
                    onClick={() => setShowInactive(!showInactive)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      showInactive ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                    aria-label="Toggle inactive programs"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        showInactive ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
              <div className="hidden lg:block flex-shrink-0">
                <ProgramTypeScrollNav
                  types={visibleProgramTypes}
                  activeType={desktopTypeNav.activeType}
                  onSelect={desktopTypeNav.scrollToType}
                />
              </div>
              <div className="overflow-x-auto overflow-y-auto hidden lg:block lg:flex-1 lg:min-h-0">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Name
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Slug
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Billing
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Price
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Dates
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(() => {
                          if (loading && !isAddingProgram && !isEditingProgram) {
                            return (
                              <tr>
                                <td colSpan={6} className="px-6 py-8 text-center">
                                  <div className="flex items-center justify-center gap-2 text-gray-500">
                                    <svg className="animate-spin h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                                    Loading programs...
                                  </div>
                                </td>
                              </tr>
                            );
                          }
                                                    const grouped = new Map<string, Program[]>();
                          visiblePrograms.forEach((p) => {
                            const t = (p.type || 'other') as ProgramType;
                            if (!grouped.has(t)) grouped.set(t, []);
                            grouped.get(t)!.push(p);
                          });
                          const sortedTypes = Array.from(grouped.keys()).sort(compareProgramTypes);

                          return sortedTypes.flatMap((typeKey) => {
                            const rows: JSX.Element[] = [];
                            rows.push(
                              <tr
                                key={`sep-${typeKey}`}
                                ref={desktopTypeNav.setSectionRef(typeKey)}
                                style={{ backgroundColor: getProgramTypeSectionColor(typeKey), scrollMarginTop: 48 }}
                              >
                                <td colSpan={6} className="px-6 py-2 text-xs font-semibold tracking-wide text-white">
                                  <span className="mr-1.5">{getProgramTypeIcon(typeKey)}</span>
                                  {getProgramTypeDisplayName(typeKey as ProgramType)}
                                </td>
                              </tr>
                            );
                            const typePrograms = grouped.get(typeKey)!;
                            const hasAnyTags = typePrograms.some(p => p.tags && p.tags.length > 0);
                            const renderProgramRow = (program: Program, rowKey: string) => (
                              <React.Fragment key={rowKey}>
                              <tr className={selectedProgramId === program.id ? 'bg-blue-50 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'} onClick={() => setSelectedProgramId(selectedProgramId === program.id ? null : program.id)}>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900 flex flex-wrap items-center gap-1.5">
                                      <span>{program.name}</span>
                                      <AdminBrandTag brand={program.brand_key} />
                                      <LocationPill name={programLocationName(program)} />
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {getScheduleDescription(program.schedule_cron)}
                                    </div>
                                    {program.tags && program.tags.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {program.tags.slice(0, 3).map((tag, index) => (
                                          <span
                                            key={index}
                                            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                                          >
                                            {tag}
                                          </span>
                                        ))}
                                        {program.tags.length > 3 && (
                                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                            +{program.tags.length - 3} more
                                          </span>
                                        )}
                                      </div>
                                    )}
                                    {program.levels && program.levels.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {program.levels.slice(0, 3).map((lvl, index) => (
                                          <span
                                            key={`lvl-${index}`}
                                            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                                          >
                                            {lvl}
                                          </span>
                                        ))}
                                        {program.levels.length > 3 && (
                                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                                            +{program.levels.length - 3} more
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                      <button
                                        onClick={() => copySlugUrl(program.name)}
                                        className="text-blue-600 hover:text-blue-800 font-mono text-xs bg-gray-50 px-2 py-1 rounded border hover:bg-gray-100 transition-colors"
                                        title="Click to copy full URL"
                                      >
                                        {programNameToSlug(program.name)}
                                      </button>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                      Click to copy URL
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                      {program.billing_type === 'recurring_monthly' ? 'Monthly' :
                                       program.billing_type === 'fixed_term' ? 'Fixed Term' :
                                       program.billing_type === 'flex_monthly' ? 'Flex Monthly' : 'Per Day'}
                                    </div>
                                    {program.invoice_due_date && (
                                      <div className="text-xs text-gray-500">
                                        Due: {formatDateDisplay(program.invoice_due_date)}
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                      ${program.price.toFixed(2)}
                                    </div>
                                    {getDiscountedPriceLabel(program) !== null && (
                                      <div className="text-xs text-green-700">
                                        {getDiscountedPriceLabel(program)}
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-500">
                                      {formatDateDisplay(program.start_date)} - {formatDateDisplay(program.end_date)}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      Duration: {program.duration} mins
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex flex-col items-start gap-1">
                                      <span
                                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                          program.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                        }`}
                                      >
                                        {program.active ? 'Active' : 'Inactive'}
                                      </span>
                                      {/* Secondary badge, not a replacement: a locked program is still Active. */}
                                      {program.active && isEnrollmentLocked(program) && (
                                        <span className="px-2 py-0.5 inline-flex items-center gap-1 text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800">
                                          <Lock className="h-3 w-3" />Enrollment Closed
                                        </span>
                                      )}
                                      <ProgramSeatsBadge status={capacityById.get(program.id)} />
                                    </div>
                                  </td>
                                </tr>
                              {selectedProgramId === program.id && (
                                <tr key={`${rowKey}-actions`} className="bg-blue-50">
                                  <td colSpan={6} className="px-6 py-2 border-t border-blue-100">
                                    <div className="flex flex-wrap gap-2">
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleProgramEditClick(program); }}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                                      >
                                        <Edit className="h-3 w-3 mr-1" />
                                        Edit
                                      </button>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); openWaiverModal(program); }}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-md hover:bg-teal-100 hover:text-teal-900 transition-colors"
                                      >
                                        <FileText className="h-3 w-3 mr-1" />
                                        Waiver
                                      </button>
                                      {program.type === 'camp' && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); setScheduleProgram(program); }}
                                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 hover:text-indigo-900 transition-colors"
                                        >
                                          <CalendarDays className="h-3 w-3 mr-1" />
                                          Schedule
                                        </button>
                                      )}
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleCloneProgram(program); }}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 hover:text-purple-900 transition-colors"
                                      >
                                        <Copy className="h-3 w-3 mr-1" />
                                        Clone
                                      </button>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); generateQRCode(program.name); }}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 hover:text-orange-900 transition-colors"
                                      >
                                        <QrCode className="h-3 w-3 mr-1" />
                                        QR Code
                                      </button>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleToggleProgramStatus(program.id, program.active); }}
                                        className={`inline-flex items-center px-2 py-1 text-xs font-medium transition-colors ${
                                          program.active
                                            ? 'text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900'
                                            : 'text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 hover:text-green-900'
                                        }`}
                                      >
                                        {program.active ? <PowerOff className="h-3 w-3 mr-1" /> : <Power className="h-3 w-3 mr-1" />}
                                        {program.active ? 'Deactivate' : 'Activate'}
                                      </button>
                                      {program.active && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleToggleEnrollmentStatus(program.id, isEnrollmentLocked(program)); }}
                                          title={isEnrollmentLocked(program)
                                            ? 'Reopen this program to new parent enrollment'
                                            : 'Stop accepting new parent enrollment. The program keeps running and billing; admins can still enroll on behalf.'}
                                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100 hover:text-amber-900 transition-colors"
                                        >
                                          {isEnrollmentLocked(program) ? <Unlock className="h-3 w-3 mr-1" /> : <Lock className="h-3 w-3 mr-1" />}
                                          {isEnrollmentLocked(program) ? 'Reopen Enrollment' : 'Close Enrollment'}
                                        </button>
                                      )}
                                      <button
                                        onClick={(e) => { e.stopPropagation(); openEnrollOnBehalf(program); }}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 hover:text-green-900 transition-colors"
                                      >
                                        Enroll on behalf
                                      </button>
                                    </div>
                                    <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                                      <ProgramDetailPanel program={program} />
                                    </div>
                                    {program.capacity != null && (
                                      <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                                        <ProgramWaitlistPanel program={program} />
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              )}
                              </React.Fragment>
                            );
                            if (hasAnyTags) {
                              const tagOrder: string[] = [];
                              const tagMap = new Map<string, Program[]>();
                              const untagged: Program[] = [];
                              typePrograms.forEach((p) => {
                                if (!p.tags || p.tags.length === 0) {
                                  untagged.push(p);
                                } else {
                                  p.tags.forEach((tag) => {
                                    if (!tagMap.has(tag)) { tagOrder.push(tag); tagMap.set(tag, []); }
                                    tagMap.get(tag)!.push(p);
                                  });
                                }
                              });
                              tagOrder.sort((a, b) => a.localeCompare(b));
                              tagMap.forEach((progs) => progs.sort((a, b) => a.name.localeCompare(b.name)));
                              untagged.sort((a, b) => a.name.localeCompare(b.name));
                              untagged.forEach((p) => rows.push(renderProgramRow(p, p.id)));
                              tagOrder.forEach((tag) => {
                                rows.push(
                                  <tr key={`tag-sep-${typeKey}-${tag}`}>
                                    <td colSpan={8} className="px-6 py-1.5 bg-gray-50 border-t border-gray-100">
                                      <div className="flex items-center gap-3">
                                        <div className="h-px flex-1 bg-gray-200" />
                                        <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">{tag}</span>
                                        <div className="h-px flex-1 bg-gray-200" />
                                      </div>
                                    </td>
                                  </tr>
                                );
                                tagMap.get(tag)!.forEach((p) => rows.push(renderProgramRow(p, `${tag}-${p.id}`)));
                              });
                            } else {
                              typePrograms.forEach((p) => rows.push(renderProgramRow(p, p.id)));
                            }
                            return rows;
                          });
                        })()}
                      </tbody>
                    </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden sticky top-14 z-20">
                <ProgramTypeScrollNav
                  types={visibleProgramTypes}
                  activeType={mobileTypeNav.activeType}
                  onSelect={mobileTypeNav.scrollToType}
                />
              </div>
              <div className="lg:hidden border-b border-gray-200">
                      {(() => {
                        if (loading && !isAddingProgram && !isEditingProgram) {
                          return (
                            <div className="flex items-center justify-center gap-2 py-8 text-gray-500">
                              <svg className="animate-spin h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                              Loading programs...
                            </div>
                          );
                        }
                                                const grouped = new Map<string, Program[]>();
                        visiblePrograms.forEach((p) => {
                          const t = (p.type || 'other') as ProgramType;
                          if (!grouped.has(t)) grouped.set(t, []);
                          grouped.get(t)!.push(p);
                        });
                        const sortedTypes = Array.from(grouped.keys()).sort(compareProgramTypes);
                        return sortedTypes.flatMap((typeKey) => {
                          const section: JSX.Element[] = [];
                          section.push(
                            <div key={`mobile-sep-${typeKey}`} ref={mobileTypeNav.setSectionRef(typeKey)} className="px-3 py-2 text-xs font-semibold text-white" style={{ backgroundColor: getProgramTypeSectionColor(typeKey), scrollMarginTop: 104 }}>
                              <span className="mr-1.5">{getProgramTypeIcon(typeKey)}</span>
                              {getProgramTypeDisplayName(typeKey as ProgramType)}
                            </div>
                          );
                          const typeProgramsMobile = grouped.get(typeKey)!;
                          const hasAnyTagsMobile = typeProgramsMobile.some(p => p.tags && p.tags.length > 0);
                          const renderProgramCard = (program: Program, cardKey: string) => (
                            <div key={cardKey} className={`border-b border-gray-200 p-3 cursor-pointer ${selectedProgramId === program.id ? 'bg-blue-50' : ''}`} onClick={() => setSelectedProgramId(selectedProgramId === program.id ? null : program.id)}>
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <div className="text-base font-bold text-gray-900 flex flex-wrap items-center gap-1">
                                <span className="break-words">{program.name}</span>
                                <AdminBrandTag brand={program.brand_key} />
                                <LocationPill name={programLocationName(program)} />
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  program.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  {program.active ? 'Active' : 'Inactive'}
                                </span>
                                <ProgramSeatsBadge status={capacityById.get(program.id)} />
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                <span>{program.type}</span>
                                <span className="mx-1.5">|</span>
                                <span>${program.price.toFixed(2)}{getDiscountedPriceLabel(program) !== null && <span className="text-green-700"> ({getDiscountedPriceLabel(program)})</span>}</span>
                                <span className="mx-1.5">|</span>
                                <span>{program.billing_type === 'recurring_monthly' ? 'Monthly' : program.billing_type === 'fixed_term' ? 'Fixed' : program.billing_type === 'flex_monthly' ? 'Flex' : 'Per Day'}</span>
                              </div>
                              {program.levels && program.levels.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {program.levels.map((lvl, index) => (
                                    <span key={`lvlm-${index}`} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
                                      {lvl}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="text-sm space-y-1">
                            <div>
                              <span className="font-medium text-gray-500">Schedule: </span>
                              <span className="text-gray-800">{getScheduleDescription(program.schedule_cron)}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-500">Dates: </span>
                              <span className="text-gray-800">{formatDateDisplay(program.start_date)} - {formatDateDisplay(program.end_date)}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-500">Slug: </span>
                              <button
                                onClick={(e) => { e.stopPropagation(); copySlugUrl(program.name); }}
                                className="text-blue-600 hover:text-blue-800 font-mono text-xs bg-gray-50 px-2 py-1 rounded border hover:bg-gray-100 transition-colors"
                                title="Click to copy full URL"
                              >
                                {programNameToSlug(program.name)}
                              </button>
                            </div>
                            {(program.marketing_metadata?.website || program.marketing_metadata?.poster_image) && (
                              <div className="text-xs text-gray-500">
                                {program.marketing_metadata.website && (
                                  <div className="truncate"><span className="font-medium text-gray-600">URL: </span>{program.marketing_metadata.website}</div>
                                )}
                                {program.marketing_metadata.poster_image && (
                                  <div className="truncate"><span className="font-medium text-gray-600">Poster: </span>{program.marketing_metadata.poster_image}</div>
                                )}
                              </div>
                            )}
                          </div>

                          {program.tags && program.tags.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {program.tags.map((tag, index) => (
                                <span key={index} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}

                          {selectedProgramId === program.id && (
                            <div className="mt-2 pt-2 border-t border-blue-100 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                              <button onClick={() => handleProgramEditClick(program)} className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors">
                                <Edit className="h-3 w-3 mr-1" />Edit
                              </button>
                              <button onClick={() => openWaiverModal(program)} className="inline-flex items-center px-2 py-1 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-md hover:bg-teal-100 hover:text-teal-900 transition-colors">
                                <FileText className="h-3 w-3 mr-1" />Waiver
                              </button>
                              {program.type === 'camp' && (
                                <button onClick={() => setScheduleProgram(program)} className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 hover:text-indigo-900 transition-colors">
                                  <CalendarDays className="h-3 w-3 mr-1" />Schedule
                                </button>
                              )}
                              <button onClick={() => handleCloneProgram(program)} className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 hover:text-purple-900 transition-colors">
                                <Copy className="h-3 w-3 mr-1" />Clone
                              </button>
                              <button onClick={() => generateQRCode(program.name)} className="inline-flex items-center px-2 py-1 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 hover:text-orange-900 transition-colors">
                                <QrCode className="h-3 w-3 mr-1" />QR Code
                              </button>
                              <button onClick={() => handleToggleProgramStatus(program.id, program.active)} className={`inline-flex items-center px-2 py-1 text-xs font-medium transition-colors ${
                                program.active
                                  ? 'text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900'
                                  : 'text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 hover:text-green-900'
                              }`}>
                                {program.active ? <PowerOff className="h-3 w-3 mr-1" /> : <Power className="h-3 w-3 mr-1" />}
                                {program.active ? 'Deactivate' : 'Activate'}
                              </button>
                              {program.active && (
                                <button onClick={() => handleToggleEnrollmentStatus(program.id, isEnrollmentLocked(program))} className="inline-flex items-center px-2 py-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100 hover:text-amber-900 transition-colors">
                                  {isEnrollmentLocked(program) ? <Unlock className="h-3 w-3 mr-1" /> : <Lock className="h-3 w-3 mr-1" />}
                                  {isEnrollmentLocked(program) ? 'Reopen Enrollment' : 'Close Enrollment'}
                                </button>
                              )}
                              <button onClick={() => openEnrollOnBehalf(program)} className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 hover:text-green-900 transition-colors">
                                Enroll on behalf
                              </button>
                            </div>
                          )}
                          {selectedProgramId === program.id && (
                            <div className="mt-2 pt-2 border-t border-blue-100" onClick={(e) => e.stopPropagation()}>
                              <ProgramDetailPanel program={program} />
                              {program.capacity != null && (
                                <div className="mt-3">
                                  <ProgramWaitlistPanel program={program} />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                          );
                          if (hasAnyTagsMobile) {
                            const tagOrderM: string[] = [];
                            const tagMapM = new Map<string, Program[]>();
                            const untaggedM: Program[] = [];
                            typeProgramsMobile.forEach((p) => {
                              if (!p.tags || p.tags.length === 0) {
                                untaggedM.push(p);
                              } else {
                                p.tags.forEach((tag) => {
                                  if (!tagMapM.has(tag)) { tagOrderM.push(tag); tagMapM.set(tag, []); }
                                  tagMapM.get(tag)!.push(p);
                                });
                              }
                            });
                            tagOrderM.sort((a, b) => a.localeCompare(b));
                            tagMapM.forEach((progs) => progs.sort((a, b) => a.name.localeCompare(b.name)));
                            untaggedM.sort((a, b) => a.name.localeCompare(b.name));
                            untaggedM.forEach((p) => section.push(renderProgramCard(p, p.id)));
                            tagOrderM.forEach((tag) => {
                              section.push(
                                <div key={`mobile-tag-sep-${typeKey}-${tag}`} className="flex items-center gap-3 px-3 py-1.5 bg-gray-50 border-t border-gray-100">
                                  <div className="h-px flex-1 bg-gray-200" />
                                  <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">{tag}</span>
                                  <div className="h-px flex-1 bg-gray-200" />
                                </div>
                              );
                              tagMapM.get(tag)!.forEach((p) => section.push(renderProgramCard(p, `${tag}-${p.id}`)));
                            });
                          } else {
                            typeProgramsMobile.forEach((p) => section.push(renderProgramCard(p, p.id)));
                          }
                          return section;
                        });
                      })()}
              </div>
            </div>
          </>
        )}

        {activeSubTab === 'line-items' && (
          <div className="lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
            <AdminLineItemConfigs
              triggerAddMode={showAddLineItemConfigForm}
              onFormClose={() => setShowAddLineItemConfigForm(false)}
            />
          </div>
        )}
      </div>
      <AdminEnrollOnBehalfModal
        isOpen={isEnrollOnBehalfOpen}
        onClose={closeEnrollOnBehalf}
        program={enrollProgram}
        parentSearch={parentSearch}
        setParentSearch={setParentSearch}
        parentSearchResults={parentSearchResults}
        searchingParents={searchingParents}
        selectedParentId={selectedParentId}
        selectedParentInfo={selectedParentInfo}
        onParentChange={handleParentChange}
        students={parentStudents}
        selectedStudentId={selectedStudentId}
        onStudentChange={setSelectedStudentId}
        optedOutItems={optedOutItems}
        setOptedOutItems={setOptedOutItems}
        enrollmentCondition={enrollmentCondition}
        enrollmentStartDate={enrollmentStartDate}
        setEnrollmentStartDate={setEnrollmentStartDate}
        selectedDays={selectedDays}
        setSelectedDays={setSelectedDays}
        sessionId={sessionId}
      />
    </AdminLayout>
  );
} 

// Admin Enroll-on-behalf Modal
export function AdminEnrollOnBehalfModal({
  isOpen,
  onClose,
  program,
  parentSearch,
  setParentSearch,
  parentSearchResults,
  searchingParents,
  selectedParentId,
  selectedParentInfo,
  onParentChange,
  students,
  selectedStudentId,
  onStudentChange,
  optedOutItems,
  setOptedOutItems,
  enrollmentCondition,
  enrollmentStartDate,
  setEnrollmentStartDate,
  selectedDays,
  setSelectedDays,
  sessionId,
}: {
  isOpen: boolean;
  onClose: () => void;
  program: Program | null;
  parentSearch: string;
  setParentSearch: React.Dispatch<React.SetStateAction<string>>;
  parentSearchResults: ParentSearchResult[];
  searchingParents: boolean;
  selectedParentId: string;
  selectedParentInfo: ParentSearchResult | null;
  onParentChange: (id: string) => void;
  students: StudentSummary[];
  selectedStudentId: string;
  onStudentChange: (id: string) => void;
  optedOutItems: string[];
  setOptedOutItems: (ids: string[]) => void;
  enrollmentCondition: 'upcoming' | 'ongoing' | 'ended' | 'loading';
  enrollmentStartDate: string;
  setEnrollmentStartDate: (d: string) => void;
  selectedDays: string[];
  setSelectedDays: (ds: string[]) => void;
  sessionId: string;
}) {
  const [selectedLevel, setSelectedLevel] = useState<string>('');

  // create_invoice_on_enrollment() only bills an eligibility discount (BHCS Student, Foster
  // City Resident) when the student qualifies. Mirror that rule here, otherwise the admin is
  // quoted a total the generated invoice won't match.
  const eligibilityDiscount = findEligibilityDiscount((program?.line_items || []).map((li) => li.line_item_config?.subtype));
  const { data: studentQualifies } = useQuery({
    queryKey: ['discount-eligibility', eligibilityDiscount?.subtype, selectedStudentId],
    queryFn: () => fetchDiscountEligibility(supabase, eligibilityDiscount!.subtype, selectedStudentId),
    enabled: !!selectedStudentId && !!eligibilityDiscount,
  });
  const discount = resolveDiscountVisibility({
    hasDiscount: !!eligibilityDiscount,
    // Matches the parent surfaces: advertise both prices until a student is chosen, then
    // quote only the one that applies. Both are shown side by side rather than folded into
    // one Total, so no single number is presented as the amount that will be invoiced.
    showBeforeStudentSelected: true,
    studentSelected: !!selectedStudentId,
    studentQualifies,
  });
  // Admins may enroll past the cap (late adds, exceptions); say so before they do (BHCS-124).
  const seats = useProgramSeats(program?.id, program?.capacity, { includeMine: false });

  if (!program) return null;

  const isDiscountLine = (li: ProgramLineItem) => isEligibilityDiscountSubtype(li.line_item_config?.subtype);
  /** Counts toward the quoted total: not opted out, and the discount only if it applies. */
  const countsTowardTotal = (li: ProgramLineItem) =>
    !optedOutItems.includes(li.line_item_config_id) && (!isDiscountLine(li) || discount.applyDiscountToTotal);

  const materialsTotal = (program.line_items || [])
    .filter((li) => li.line_item_config?.type === 'materials' && !optedOutItems.includes(li.line_item_config_id))
    .reduce((sum, li) => sum + li.amount, 0);
  const tuitionPerDay = (program.line_items || []).find((li) => li.line_item_config?.type === 'tuition')?.amount || 0;
  const currentPrice = (program.line_items || [])
    .filter(countsTowardTotal)
    .reduce((sum, li) => sum + li.amount, 0);
  // Flex: only day-counted lines (tuition + per-day fees) belong in the per-day total;
  // flat fees (deposit, …) join materials as one-off-at-enrollment charges.
  const isFlexProgram = program.billing_type === 'flex_monthly';
  const flexFlatTotal = isFlexProgram
    ? (program.line_items || [])
        .filter((li) => countsTowardTotal(li)
          && li.line_item_config?.type !== 'materials'
          && !isFlexDayCounted(li.line_item_config?.type, li.line_item_config?.subtype))
        .reduce((sum, li) => sum + li.amount, 0)
    : 0;
  const perDaySuffix = program.billing_type === 'per_day' || isFlexProgram;
  const flexOneOffTotal = materialsTotal + flexFlatTotal;

  // While advertising, currentPrice excludes the discount, so it is the regular price and
  // the discounted price is derived from it. Once a student is chosen, currentPrice already
  // reflects their eligibility and only the single Total below is rendered.
  const discountAmount = (program.line_items || [])
    .filter((li) => isDiscountLine(li) && !optedOutItems.includes(li.line_item_config_id))
    .reduce((sum, li) => sum + li.amount, 0);
  const regularTotal = currentPrice - flexOneOffTotal;
  const discountedTotal = regularTotal + discountAmount;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Enroll a Student">
      <div className="space-y-6">
        {seats.isFull && seats.status && (
          <div className="p-3 bg-amber-50 border-l-4 border-amber-400 rounded-md text-sm text-amber-900" role="status">
            {program.name} is full ({describeSeats(seats.status)}
            {seats.status.seats_held > 0 ? `, ${seats.status.seats_held} held for waitlist offers` : ''}).
            Enrolling this student puts it over capacity. Parents cannot do this; you can.
          </div>
        )}
        <div>
          <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-4">Pricing Details</h3>
          <div className="divide-y divide-gray-200 bg-gray-50 rounded-lg p-3 sm:p-4">
            {/* Required items */}
            {(program.line_items || []).filter((li) => !li.is_optional && (!isDiscountLine(li) || discount.showDiscountLine)).map((item) => (
              <div key={item.line_item_config_id} className="py-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0 pr-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm sm:text-base font-medium text-gray-900">{item.line_item_config?.name}</span>
                      {item.line_item_config?.type && (
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 whitespace-nowrap">
                          {item.line_item_config?.type}
                          {item.line_item_config?.type === 'materials' && ' (one-off charge on enrollment)'}
                        </span>
                      )}
                    </div>
                    {item.line_item_config?.description && (
                      <span className="block text-xs text-gray-500 mt-1">{item.line_item_config?.description}</span>
                    )}
                  </div>
                  <div className={`font-medium whitespace-nowrap ${
                    item.amount < 0 ? 'text-green-600' : 'text-gray-900'
                  }`}>
                    {item.amount < 0 ? '-' : ''}${Math.abs(item.amount).toFixed(2)}{((program.billing_type === 'per_day' && item.line_item_config?.type === 'tuition') || (program.billing_type === 'flex_monthly' && isFlexDayCounted(item.line_item_config?.type, item.line_item_config?.subtype))) && '/day'}
                  </div>
                </div>
              </div>
            ))}

            {/* Optional add-ons */}
            {(program.line_items || []).some((li) => li.is_optional) && (
              <div className="py-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Optional Add-ons</span>
              </div>
            )}
            {(program.line_items || []).filter((li) => li.is_optional).map((item) => (
              <div key={item.line_item_config_id} className="py-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0 pr-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm sm:text-base font-medium text-gray-900">{item.line_item_config?.name}</span>
                      {item.line_item_config?.type && (
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 whitespace-nowrap">
                          {item.line_item_config?.type}
                          {item.line_item_config?.type === 'materials' && ' (one-off charge on enrollment)'}
                        </span>
                      )}
                    </div>
                    {item.line_item_config?.description && (
                      <span className="block text-xs text-gray-500 mt-1">{item.line_item_config?.description}</span>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <label className="inline-flex items-center whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        checked={!optedOutItems.includes(item.line_item_config_id)}
                        onChange={(e) => {
                          const updated = e.target.checked
                            ? optedOutItems.filter((id) => id !== item.line_item_config_id)
                            : [...optedOutItems, item.line_item_config_id];
                          setOptedOutItems(updated);
                        }}
                      />
                      <span className="ml-2 text-xs sm:text-sm text-gray-600">Include</span>
                    </label>
                    <div className={`font-medium whitespace-nowrap ${
                      optedOutItems.includes(item.line_item_config_id)
                        ? 'text-gray-400'
                        : 'text-gray-900'
                    }`}>
                      ${item.amount.toFixed(2)}{((program.billing_type === 'per_day' && item.line_item_config?.type === 'tuition') || (program.billing_type === 'flex_monthly' && isFlexDayCounted(item.line_item_config?.type, item.line_item_config?.subtype))) && '/day'}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <div className="py-3 flex flex-col">
              {discount.showDualPrices ? (
                /* No student chosen yet — show both prices so neither reads as the quote. */
                <>
                  <div className="flex justify-between items-center font-medium text-sm sm:text-base">
                    <span>Regular Price</span>
                    <span>${regularTotal.toFixed(2)}{perDaySuffix && '/day'}</span>
                  </div>
                  <div className="flex justify-between items-center font-medium text-sm sm:text-base mt-1">
                    <span className="text-green-700">{eligibilityDiscount?.priceLabel}</span>
                    <span className="text-green-700">${discountedTotal.toFixed(2)}{perDaySuffix && '/day'}</span>
                  </div>
                  <div className="text-xs text-gray-500 text-right mt-1">
                    Select a student to see which price applies.
                  </div>
                  {flexOneOffTotal > 0 && (
                    <div className="text-xs sm:text-sm text-gray-600 text-right mt-1">
                      + ${flexOneOffTotal.toFixed(2)} one-off charge on enrollment
                    </div>
                  )}
                </>
              ) : (
                <div className="flex justify-between items-center font-medium text-sm sm:text-base">
                  <span className={discount.applyDiscountToTotal ? 'text-green-700' : ''}>Total</span>
                  <div className="flex flex-col items-end">
                    <span className={discount.applyDiscountToTotal ? 'text-green-700' : ''}>
                      ${regularTotal.toFixed(2)}{perDaySuffix && '/day'}
                    </span>
                    {flexOneOffTotal > 0 && (
                      <span className="text-xs sm:text-sm text-gray-600">
                        + ${flexOneOffTotal.toFixed(2)} one-off charge on enrollment
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-6 space-y-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Select Parent</label>
            {selectedParentId ? (
              <div className="flex items-center justify-between p-3 border border-green-200 bg-green-50 rounded-md">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {selectedParentInfo?.first_name} {selectedParentInfo?.last_name}
                  </div>
                  <div className="text-xs text-gray-500">{selectedParentInfo?.email}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onParentChange('')}
                  className="text-sm text-red-600 hover:text-red-800"
                >
                  Change
                </button>
              </div>
            ) : (
              <div>
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={parentSearch}
                  onChange={(e) => setParentSearch(e.target.value)}
                />
                
                {searchingParents && (
                  <div className="text-sm text-gray-500 mt-2">Searching...</div>
                )}

                {parentSearchResults.length > 0 && (
                  <div className="mt-2 space-y-1 max-h-40 overflow-y-auto border border-gray-200 rounded-md">
                    {parentSearchResults.map((parent) => (
                      <div
                        key={parent.id}
                        className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer"
                        onClick={() => onParentChange(parent.id)}
                      >
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {parent.first_name} {parent.last_name}
                          </div>
                          <div className="text-xs text-gray-500">{parent.email}</div>
                        </div>
                        <button
                          type="button"
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          Select
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {parentSearch.length >= 2 && !searchingParents && parentSearchResults.length === 0 && (
                  <div className="text-sm text-gray-500 mt-2">No parents found.</div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Select Student</label>
            <select
              // The visible label above is not programmatically associated, so the control had
              // no accessible name -- screen readers announced an unlabelled combobox, and
              // tests could only reach it as "the" combobox on the page.
              aria-label="Select Student"
              className="w-full border rounded px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              value={selectedStudentId}
              onChange={(e) => onStudentChange(e.target.value)}
              disabled={!selectedParentId || students.length === 0}
            >
              <option value="">-- Select a student --</option>
              {students.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.first_name} {s.last_name} (DOB: {s.date_of_birth ? formatDateDisplay(s.date_of_birth) : 'Not provided'})
                </option>
              ))}
            </select>
            {Array.isArray(program.levels) && program.levels.length > 0 && (
              <div className="mt-4">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Select Level</label>
                <select
                  className="w-full border rounded px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  value={selectedLevel}
                  onChange={(e) => setSelectedLevel(e.target.value)}
                >
                  <option value="">-- Select a level --</option>
                  {program.levels.map((lvl) => (
                    <option key={lvl} value={lvl}>{lvl}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {enrollmentCondition === 'ongoing' && (
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Enrollment Start Date</label>
              <input
                type="date"
                value={enrollmentStartDate}
                min={program.start_date > getTodayDateString() ? program.start_date : getTodayDateString()}
                max={program.end_date}
                onChange={(e) => setEnrollmentStartDate(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Select a start date between {formatDateDisplay(program.start_date > getTodayDateString() ? program.start_date : getTodayDateString())} and program end ({formatDateDisplay(program.end_date)}).
              </p>
            </div>
          )}

          {program.billing_type === 'per_day' && (
            <div className="mb-4">
              <h4 className="text-xs sm:text-sm font-medium text-gray-900 mb-3">Select Days to Attend</h4>
              <p className="text-xs sm:text-sm text-gray-600 mb-4">
                Choose days to attend. You'll be charged ${(tuitionPerDay - materialsTotal).toFixed(2)}/day per day selected.
              </p>
              <DaySelector
                startDate={program.start_date}
                endDate={program.end_date}
                selectedDays={selectedDays}
                onChange={setSelectedDays}
                minSelections={1}
                scheduleCron={program.schedule_cron}
                isSelectableDate={sessionDayPredicate(program)}
              />
            </div>
          )}

          {/* Enroll form */}
          <div className="mt-4">
            <EnrollStudent
              programId={program.id}
              selectedStudentId={selectedStudentId || null}
              enrollmentStartDate={program.billing_type === 'per_day' && selectedDays.length > 0 ? selectedDays[0] : enrollmentStartDate}
              enrollmentEndDate={program.billing_type === 'per_day' && selectedDays.length > 0 ? selectedDays[selectedDays.length - 1] : program.end_date}
              selectedDays={program.billing_type === 'per_day' ? selectedDays : undefined}
              billingType={program.billing_type}
              optedOutItems={optedOutItems}
              optionalItemIds={(program.line_items || [])
                .filter((li) => li.is_optional)
                .map((li) => li.line_item_config_id)}
              onSuccess={onClose}
              sessionId={sessionId}
              overrideParentId={selectedParentId || undefined}
              actorType="admin"
              availableLevels={Array.isArray(program.levels) ? program.levels : undefined}
              selectedLevel={selectedLevel}
              programName={program.name}
              programType={program.type}
              programSchedule={program.schedule_cron ? getScheduleTimeRange(program.schedule_cron) : undefined}
              scheduleCron={program.schedule_cron}
              brandKey={program.brand_key}
            />
          </div>

          
        </div>
      </div>
    </Modal>
  );
}